import eveningRoutineAsset from "@/assets/ova-bundles/evening-routine.webp.asset.json";
import liverBotanicalAsset from "@/assets/ova-bundles/liver-botanical.webp.asset.json";
import magEveningAsset from "@/assets/ova-bundles/mag-evening.webp.asset.json";
import morningRoutineAsset from "@/assets/ova-bundles/morning-routine.webp.asset.json";
import ovaRitualAsset from "@/assets/ova-bundles/ova-ritual.webp.asset.json";

/**
 * Approved Ova-centred bundle storytelling. Commerce values and availability
 * come from the resolved BundlePageData and purchase controls, never this file.
 * Membership is matched by exact product handles, independent of bundle slug.
 */
export type OvaBundleKind = "mag" | "liver" | "trio";
export type OvaProductHandle = "ova-balance" | "mag-absorb-pro" | "liver-biome-plus";
export type OvaSceneId = "inositol" | "metabolic" | "mag" | "liver" | "gut" | "cofactors";
export type ProductHandle = OvaProductHandle;
export type SceneId = OvaSceneId;

export interface BundleImage {
  src: string;
  alt: string;
  position: string;
}

export interface BundleCopyRow {
  title: string;
  body: string;
}

export interface OvaBundleContent {
  /** Editorial name only: use the resolved rule title for the actual offer. */
  name: string;
  headlineLines: string[];
  intro: string;
  pills: string[];
  heroNote: string;
  strip: string;
  whyIntro: string;
  together: string;
  handles: OvaProductHandle[];
  sceneIds: OvaSceneId[];
  routineTitleLines: string[];
  /** Daily directions are independent of the number of packs ordered. */
  routine: BundleCopyRow[];
  closeLines: string[];
  heroImage: BundleImage;
  routineImage: BundleImage;
}

export interface ProductContribution {
  name: string;
  role: string;
  body: string;
  unitsPerPack: number;
  unit: "sachets" | "tablets";
}

export interface Ingredient {
  name: string;
  dose: string;
  body: string;
}

export interface IngredientGroup {
  title: string;
  items: Ingredient[];
}

export interface IngredientScene {
  id: OvaSceneId;
  title: string;
  body: string;
  detail: string;
  dose: string;
  main: string;
  sub: string;
  accent: "purple" | "blue" | "green";
  image: BundleImage;
}

export interface BundleSupplyItem {
  handle: OvaProductHandle;
  name: string;
  packCount: number;
  unitCount: number;
  unit: "sachets" | "tablets";
  dailyUnits: number;
  days: number;
  copy: string;
}

export interface BundleSupply {
  quantityPerItem: number;
  items: BundleSupplyItem[];
  pairedDays: number;
  magTabletsRemaining: number;
  contents: string[];
  duration: string;
  refill: BundleCopyRow;
}

export interface BundleFaq {
  question: string;
  answer: string;
}

export const ovaBundleContent: Record<OvaBundleKind, OvaBundleContent> = {
  "mag": {
    "name": "Ova + Mag · Cycle & Calm Duo",
    "headlineLines": [
      "Support your cycle.",
      "Make room for calm."
    ],
    "intro": "Cycle support with an evening companion. Ova brings the inositol-led foundation. Mag adds magnesium for relaxation and muscle support—just one tablet alongside Ova.",
    "pills": [
      "Cycle & metabolic support",
      "Relaxation & recovery"
    ],
    "heroNote": "1 Ova sachet + 1 Mag tablet per day in this paired routine.",
    "strip": "CYCLE & METABOLIC SUPPORT  +  EVENING RELAXATION  ·  ONE CONSIDERED ROUTINE",
    "whyIntro": "Choose this pair when you want a cycle-focused foundation and an evening magnesium routine, with clear directions for using them together.",
    "together": "Ova keeps cycle and metabolic support at the centre. Mag adds a dedicated relaxation and muscle-support layer, with just one tablet in the paired routine.",
    "handles": [
      "ova-balance",
      "mag-absorb-pro"
    ],
    "sceneIds": [
      "inositol",
      "metabolic",
      "mag",
      "cofactors"
    ],
    "routineTitleLines": [
      "A moment with a meal.",
      "A moment to wind down."
    ],
    "routine": [
      {
        "title": "With a meal",
        "body": "Mix one Ova sachet into water and take with food. Anchor it to a meal you have every day."
      },
      {
        "title": "In the evening",
        "body": "Take one Mag tablet with water as part of this paired routine."
      }
    ],
    "closeLines": [
      "Give your cycle routine",
      "an evening companion."
    ],
    "heroImage": {
      "src": morningRoutineAsset.url,
      "alt": "Illustrative woman preparing her daily drink at a sunlit table",
      "position": "58% 50%"
    },
    "routineImage": {
      "src": eveningRoutineAsset.url,
      "alt": "Illustrative woman winding down with a book in warm evening light",
      "position": "50% 50%"
    }
  },
  "liver": {
    "name": "Her Balance Duo · Ova + Liver",
    "headlineLines": [
      "Cycle support.",
      "With your gut",
      "in the picture."
    ],
    "intro": "Cycle support and gut care, thoughtfully paired. Ova is your inositol-led foundation. Liver Biome Plus adds NAC, botanical actives and a probiotic blend.",
    "pills": [
      "Cycle & metabolic support",
      "Liver & gut care"
    ],
    "heroNote": "Ova Balance + Liver Biome Plus · Two distinct roles, one daily routine.",
    "strip": "CYCLE & METABOLIC SUPPORT  +  LIVER & GUT CARE  ·  THOUGHTFULLY PAIRED",
    "whyIntro": "Choose this duo when cycle support and daily gut care are both priorities. The second formula earns its place through a different ingredient set.",
    "together": "Keep Ova as your cycle-focused foundation. Add Liver Biome Plus for its NAC, botanical actives and probiotic blend, without adding a second inositol formula.",
    "handles": [
      "ova-balance",
      "liver-biome-plus"
    ],
    "sceneIds": [
      "inositol",
      "metabolic",
      "liver",
      "gut"
    ],
    "routineTitleLines": [
      "Build it around",
      "the meals you already have."
    ],
    "routine": [
      {
        "title": "Your Ova moment",
        "body": "One Ova sachet daily, mixed in water and taken with food."
      },
      {
        "title": "Your liver & gut moment",
        "body": "Two Liver Biome Plus tablets daily with food, together or divided as directed on the label."
      }
    ],
    "closeLines": [
      "A cycle foundation.",
      "A distinct gut-care addition."
    ],
    "heroImage": {
      "src": morningRoutineAsset.url,
      "alt": "Illustrative woman preparing her daily drink at a sunlit table",
      "position": "58% 50%"
    },
    "routineImage": {
      "src": morningRoutineAsset.url,
      "alt": "Illustrative woman preparing her daily drink at a sunlit table",
      "position": "58% 50%"
    }
  },
  "trio": {
    "name": "Full Reset Trio · Ova + Mag + Liver",
    "headlineLines": [
      "Cycle. Calm. Gut care.",
      "A routine that",
      "fits together."
    ],
    "intro": "Keep Ova at the centre. Add Mag for your evening magnesium routine and Liver Biome Plus for liver-and-gut support. Three formulas, each with a clear purpose.",
    "pills": [
      "Cycle foundation",
      "Evening magnesium",
      "Liver & gut care"
    ],
    "heroNote": "Three distinct formulas. A clear reason for each.",
    "strip": "OVA AT THE CENTRE  ·  MAG FOR THE EVENING  ·  LIVER BIOME PLUS FOR GUT & LIVER SUPPORT",
    "whyIntro": "Choose the trio when all three priorities matter to you. Each product has a defined purpose, so you can see exactly what the complete pairing adds.",
    "together": "A cycle-focused sachet, an evening magnesium tablet and a liver-and-gut formula. Three different roles organised into one understandable routine.",
    "handles": [
      "ova-balance",
      "mag-absorb-pro",
      "liver-biome-plus"
    ],
    "sceneIds": [
      "inositol",
      "metabolic",
      "mag",
      "liver",
      "gut"
    ],
    "routineTitleLines": [
      "Your day, with",
      "a little more structure."
    ],
    "routine": [
      {
        "title": "With a meal",
        "body": "One Ova sachet with food. Take Liver Biome Plus according to its two-tablet daily label direction."
      },
      {
        "title": "In the evening",
        "body": "The Ova + Mag component uses one Mag tablet, bringing elemental magnesium from that pair to 320 mg."
      }
    ],
    "closeLines": [
      "Choose support",
      "with a purpose."
    ],
    "heroImage": {
      "src": eveningRoutineAsset.url,
      "alt": "Illustrative woman winding down with a book in warm evening light",
      "position": "50% 50%"
    },
    "routineImage": {
      "src": morningRoutineAsset.url,
      "alt": "Illustrative woman preparing her daily drink at a sunlit table",
      "position": "58% 50%"
    }
  }
};

export const contributionsByHandle: Record<OvaProductHandle, ProductContribution> = {
  "ova-balance": {
    "name": "Ova Balance",
    "role": "The cycle foundation",
    "body": "40:1 inositol, berberine and active folate. Your daily foundation for cycle and metabolic support.",
    "unitsPerPack": 30,
    "unit": "sachets"
  },
  "mag-absorb-pro": {
    "name": "Mag Absorb Pro",
    "role": "The evening addition",
    "body": "Five magnesium forms for nerve and muscle support. Your evening addition for relaxation and recovery.",
    "unitsPerPack": 60,
    "unit": "tablets"
  },
  "liver-biome-plus": {
    "name": "Liver Biome Plus",
    "role": "The gut & liver addition",
    "body": "NAC and botanical actives, plus a 10-billion-CFU probiotic blend. A separate liver-and-gut focus.",
    "unitsPerPack": 60,
    "unit": "tablets"
  }
};

export const ingredientsByHandle: Record<OvaProductHandle, IngredientGroup> = {
  "ova-balance": {
    "title": "Ova Balance · per daily sachet",
    "items": [
      {
        "name": "Myo-inositol",
        "dose": "2,000 mg",
        "body": "Participates in intracellular signalling, including insulin-related pathways; the main inositol in the formula."
      },
      {
        "name": "D-chiro-inositol",
        "dose": "50 mg",
        "body": "The complementary inositol form that completes the 40:1 myo-to-D-chiro ratio."
      },
      {
        "name": "Berberine HCl",
        "dose": "300 mg",
        "body": "Included for the formula’s metabolic-support focus; it has been investigated for effects on glucose-related pathways."
      },
      {
        "name": "Quatrefolic® · L-5-MTHF",
        "dose": "400 mcg",
        "body": "An active folate form that supports normal cell division and folate metabolism."
      },
      {
        "name": "Magnesium bisglycinate",
        "dose": "100 mg elemental magnesium · brand confirmed",
        "body": "Contributes to normal muscle and nerve function. Counted toward the Ova + Mag daily total."
      },
      {
        "name": "Zinc bisglycinate",
        "dose": "10 mg as listed on the product page",
        "body": "Zinc supports normal immune function, cell division and many enzyme systems."
      },
      {
        "name": "Chromium picolinate",
        "dose": "200 mcg as listed on the product page",
        "body": "Included as a trace-mineral cofactor in the metabolic-support formula."
      },
      {
        "name": "Vitamin D3",
        "dose": "600 IU / 15 mcg",
        "body": "Supports calcium absorption and normal bone, muscle and immune function."
      },
      {
        "name": "Bio-Absorb Matrix",
        "dose": "Proprietary delivery-support blend",
        "body": "Part of the formula’s delivery design. Its composition is not disclosed; no absorption multiplier is claimed here."
      }
    ]
  },
  "mag-absorb-pro": {
    "title": "Mag Absorb Pro · what is in the one-tablet addition",
    "items": [
      {
        "name": "Liposomal magnesium bisglycinate",
        "dose": "One of five magnesium forms",
        "body": "A chelated magnesium source in the matrix. Its role is to contribute magnesium to the total, not a separate sedative effect."
      },
      {
        "name": "Aquamin® marine magnesium",
        "dose": "One of five magnesium forms",
        "body": "The marine-derived magnesium component of the five-form matrix."
      },
      {
        "name": "Magnesium chloride",
        "dose": "One of five magnesium forms",
        "body": "An additional source of the same essential mineral, magnesium."
      },
      {
        "name": "Magnesium aspartate",
        "dose": "One of five magnesium forms",
        "body": "Another magnesium form in the combined mineral matrix."
      },
      {
        "name": "Magnesium gluconate",
        "dose": "One of five magnesium forms",
        "body": "Completes the five-form blend; the relevant combined figure is the elemental magnesium amount."
      },
      {
        "name": "Elemental magnesium",
        "dose": "220 mg per tablet · brand confirmed",
        "body": "Supports normal muscle, nerve and energy functions. Paired with Ova’s 100 mg, the daily total is 320 mg."
      },
      {
        "name": "Zinc + copper",
        "dose": "Mineral cofactors",
        "body": "Zinc supports normal immune and enzyme function; copper participates in normal energy and iron metabolism."
      },
      {
        "name": "P-5-P · vitamin B6",
        "dose": "Vitamin cofactor",
        "body": "The active B6 form; vitamin B6 participates in amino-acid metabolism and neurotransmitter synthesis."
      },
      {
        "name": "Vitamin D3",
        "dose": "Vitamin cofactor",
        "body": "Contributes to normal bone, muscle and immune function. Also present in Ova, so the combined routine should count both."
      },
      {
        "name": "Bio-Absorb Matrix",
        "dose": "Proprietary delivery-support blend",
        "body": "The product’s delivery-support blend. Individual component doses are not inferred here."
      }
    ]
  },
  "liver-biome-plus": {
    "title": "Liver Biome Plus · per two-tablet daily serving",
    "items": [
      {
        "name": "N-acetylcysteine · NAC",
        "dose": "600 mg",
        "body": "Supplies cysteine, a building block the body uses to make glutathione."
      },
      {
        "name": "Liposomal silymarin",
        "dose": "350 mg equivalent",
        "body": "Milk-thistle-derived actives included for the liver-support focus."
      },
      {
        "name": "Liposomal curcumin",
        "dose": "100 mg",
        "body": "A turmeric-derived ingredient included in the antioxidant-support layer."
      },
      {
        "name": "Liposomal alpha-lipoic acid",
        "dose": "100 mg",
        "body": "ALA participates in cellular energy pathways and has antioxidant properties."
      },
      {
        "name": "Multi-strain probiotic blend",
        "dose": "10 billion CFU",
        "body": "The gut-focused part of the formula. The effects of probiotics depend on the specific strains and their viable dose."
      }
    ]
  }
};

export const ingredientScenes: Record<OvaSceneId, IngredientScene> = {
  "inositol": {
    "id": "inositol",
    "title": "Begin with the cycle foundation.",
    "body": "Myo-inositol and D-chiro-inositol, paired at 40:1, form Ova’s daily foundation for cycle and metabolic support.",
    "detail": "Myo-inositol and D-chiro-inositol participate in cell signalling, including insulin-related pathways. Ova puts them together in a defined 40:1 ratio for cycle and metabolic support.",
    "dose": "OVA · 2,000 mg myo + 50 mg D-chiro per sachet",
    "main": "40:1",
    "sub": "Inositol foundation",
    "accent": "purple",
    "image": {
      "src": ovaRitualAsset.url,
      "alt": "A quiet morning ritual with a ceramic powder dish and water",
      "position": "50% 65%"
    }
  },
  "metabolic": {
    "id": "metabolic",
    "title": "Give the foundation more than inositol.",
    "body": "Berberine, chromium and active folate bring distinct roles in metabolism and normal cell division.",
    "detail": "Berberine adds a metabolic-support ingredient. Chromium contributes to nutrient metabolism, while active folate supports normal cell division. Each brings a different role to the sachet.",
    "dose": "OVA · Berberine 300 mg · L-5-MTHF 400 mcg",
    "main": "Beyond\ninositol",
    "sub": "Metabolic cofactors",
    "accent": "purple",
    "image": {
      "src": morningRoutineAsset.url,
      "alt": "A woman preparing her daily drink at a sunlit table",
      "position": "50% 45%"
    }
  },
  "mag": {
    "id": "mag",
    "title": "Add magnesium with the overlap counted.",
    "body": "Ova provides 100 mg. One Mag tablet adds 220 mg: 320 mg elemental magnesium for normal nerve and muscle function.",
    "detail": "Magnesium supports normal nerve signalling, muscle function and energy metabolism. Ova contributes 100 mg; one Mag tablet adds 220 mg. Together, the paired routine supplies 320 mg elemental magnesium.",
    "dose": "OVA + MAG · 1 sachet + 1 tablet daily",
    "main": "100 + 220",
    "sub": "320 mg elemental magnesium",
    "accent": "blue",
    "image": {
      "src": magEveningAsset.url,
      "alt": "Water, linen and a closed book in soft evening light",
      "position": "50% 64%"
    }
  },
  "liver": {
    "id": "liver",
    "title": "Add a separate antioxidant layer.",
    "body": "NAC provides a building block for glutathione. Silymarin, curcumin and ALA add the formula’s botanical and antioxidant focus.",
    "detail": "NAC supplies cysteine, a building block for glutathione. Silymarin, curcumin and alpha-lipoic acid are included in Liver Biome Plus for its liver and antioxidant-support focus.",
    "dose": "LIVER · NAC 600 mg per 2-tablet serving",
    "main": "NAC",
    "sub": "Glutathione precursor",
    "accent": "green",
    "image": {
      "src": liverBotanicalAsset.url,
      "alt": "A small milk-thistle bloom resting on natural linen",
      "position": "50% 68%"
    }
  },
  "gut": {
    "id": "gut",
    "title": "Make room for the gut, too.",
    "body": "The probiotic blend adds a distinct gut-care focus alongside Ova’s cycle-support foundation.",
    "detail": "The 10-billion-CFU probiotic blend adds the gut-focused part of Liver Biome Plus. This is why the pairing makes sense when you want cycle support and a separate daily gut-care formula.",
    "dose": "LIVER · 10 billion CFU per 2-tablet serving",
    "main": "10 billion",
    "sub": "CFU probiotic blend",
    "accent": "green",
    "image": {
      "src": morningRoutineAsset.url,
      "alt": "A gentle daily ritual at home",
      "position": "65% 65%"
    }
  },
  "cofactors": {
    "id": "cofactors",
    "title": "Finish with the everyday essentials.",
    "body": "Magnesium, zinc and vitamin D3 support normal muscle, immune and metabolic functions in your daily sachet.",
    "detail": "Ova also includes magnesium, zinc and vitamin D3. These nutrients contribute to normal muscle, immune and metabolic functions alongside the inositol-led foundation.",
    "dose": "OVA · Vitamin D3 600 IU · Magnesium 100 mg",
    "main": "Daily\nessentials",
    "sub": "Minerals + vitamin D3",
    "accent": "purple",
    "image": {
      "src": ovaRitualAsset.url,
      "alt": "The quiet details of a daily routine",
      "position": "38% 76%"
    }
  }
};

/** Only exact, distinct two- or three-product memberships receive this design. */
export function getOvaBundleKind(handles: string[]): OvaBundleKind | null {
  if (handles.length < 2 || handles.length > 3) return null;
  const members = new Set(handles);
  if (members.size !== handles.length || !members.has("ova-balance")) return null;
  if (handles.some((handle) => !Object.prototype.hasOwnProperty.call(contributionsByHandle, handle))) {
    return null;
  }
  if (members.size === 3 && members.has("mag-absorb-pro") && members.has("liver-biome-plus")) {
    return "trio";
  }
  if (members.size === 2 && members.has("mag-absorb-pro")) return "mag";
  if (members.size === 2 && members.has("liver-biome-plus")) return "liver";
  return null;
}

/**
 * Quantity is the rule's equal pack count per member, not a daily dose.
 * Invalid quantities are rejected so corrupt offer data cannot produce false
 * supply claims. Call only after the bundle page's quantity validation.
 */
export function getBundleSupply(kind: OvaBundleKind, qty: number): BundleSupply {
  if (!Number.isSafeInteger(qty) || qty < 1 || qty > Number.MAX_SAFE_INTEGER / 60) {
    throw new RangeError("Bundle quantity must be a positive, safely representable integer.");
  }
  const items = ovaBundleContent[kind].handles.map((handle): BundleSupplyItem => {
    const product = contributionsByHandle[handle];
    const dailyUnits = handle === "liver-biome-plus" ? 2 : 1;
    const unitCount = product.unitsPerPack * qty;
    return {
      handle,
      name: product.name,
      packCount: qty,
      unitCount,
      unit: product.unit,
      dailyUnits,
      days: unitCount / dailyUnits,
      copy: product.name + ": " + qty + " " + (qty === 1 ? "pack" : "packs") + " · " + unitCount + " " + product.unit + ".",
    };
  });
  const pairedDays = Math.min(...items.map((item) => item.days));
  const mag = items.find((item) => item.handle === "mag-absorb-pro");
  const magTabletsRemaining = mag ? mag.unitCount - pairedDays * mag.dailyUnits : 0;
  const duration = kind === "liver"
    ? pairedDays + " days of both formulas at the stated directions."
    : pairedDays + " days of " + (kind === "trio" ? "all three" : "the combined routine") +
      " at these directions, with " + magTabletsRemaining + " Mag tablets remaining.";
  const refill: BundleCopyRow = kind === "liver"
    ? { title: "Keep the routine simple", body: qty + " " + (qty === 1 ? "pack" : "packs") + " of each provides " + pairedDays + " days at these directions." }
    : {
      title: kind === "trio" ? "Plan your next pack" : "When it is time to refill",
      body: (kind === "trio" ? "Ova and Liver each last " : "Ova lasts ") + pairedDays +
        " days. Your " + mag!.unitCount + " Mag tablets last " + mag!.days + " days at one tablet daily.",
    };
  return {
    quantityPerItem: qty,
    items,
    pairedDays,
    magTabletsRemaining,
    contents: items.map((item) => item.copy),
    duration,
    refill,
  };
}

export function getBundleRoutine(kind: OvaBundleKind, qty: number): BundleCopyRow[] {
  return [...ovaBundleContent[kind].routine, getBundleSupply(kind, qty).refill];
}

export const ovaMagDirection = {
  title: "With Ova, just one Mag tablet.",
  body: "Ova already contributes 100 mg of elemental magnesium. One Mag Absorb Pro tablet adds 220 mg, bringing this paired daily routine to 320 mg.",
  routine: "One Ova sachet with a meal. One Mag tablet in the evening. A clear routine, with the overlap already counted.",
  note: "Bundle-specific direction. Count any other magnesium supplements when reviewing your complete routine.",
  ovaElementalMg: 100,
  magElementalMgPerTablet: 220,
  combinedElementalMg: 320,
};

export interface BundleTimelineSlot {
  slot: string;
  time: string;
  product: string;
  detail: string;
}

/**
 * Visual AM/PM chrononutrition timeline. Doses follow the approved label
 * directions: Ova one sachet with food, Liver Biome Plus two tablets daily,
 * Mag Absorb Pro one tablet in the evening when paired with Ova.
 */
export function getBundleTimeline(kind: OvaBundleKind): BundleTimelineSlot[] {
  const morning: BundleTimelineSlot = {
    slot: "AM",
    time: "Morning, with breakfast",
    product: "Ova Balance",
    detail: "One sachet mixed in water, taken with food. Inositol at the 40:1 ratio with berberine and active folate is the metabolic foundation for the day.",
  };
  const midday: BundleTimelineSlot = {
    slot: "Midday",
    time: "During the day, with food",
    product: "Liver Biome Plus",
    detail: "Two tablets daily, together or divided across the day. Liver and gut support sits on the clearance side of the routine, a different job from inositol.",
  };
  const evening: BundleTimelineSlot = {
    slot: "PM",
    time: "Evening, about 45 minutes before bed",
    product: "Mag Absorb Pro",
    detail: "One tablet with water. Magnesium for PCOS is about sleep quality, cramps and evening restlessness. Ova already contributes 100 mg, so the pair totals 320 mg.",
  };
  if (kind === "mag") return [morning, evening];
  if (kind === "liver") return [morning, midday];
  return [morning, midday, evening];
}

export function getBundleFaqs(kind: OvaBundleKind, qty: number): BundleFaq[] {
  const content = ovaBundleContent[kind];
  const supply = getBundleSupply(kind, qty);
  const magnesiumQuestions: BundleFaq[] = kind !== "liver" ? [
    {
      question: "Why only one Mag tablet with Ova?",
      answer: "Ova already provides 100 mg elemental magnesium. One Mag tablet provides another 220 mg. The brand-approved Ova + Mag direction is therefore one Ova sachet plus one Mag tablet daily, providing 320 mg from this pair. This is a bundle-specific direction, not a universal magnesium requirement.",
    },
  ] : [];
  return [
    { question: "Why choose the bundle instead of Ova alone?", answer: content.together },
    {
      question: "Why isn't inositol alone enough for PCOS?",
      answer: "Inositol at the 40:1 myo:D-chiro ratio restores the insulin second-messenger signal inside the cell, and that is the foundation most PCOS routines are built on. It does not change how quickly the liver conjugates and clears hormone by-products, it does not address the gut microbiome that recirculates them, and it does not lower the evening cortisol that keeps insulin resistance running the next day. Those are separate jobs, which is why women dealing with hormonal acne, PCOS fatigue or fatty liver alongside PCOS often pair inositol with liver-and-gut or magnesium support rather than raising the inositol dose.",
    },
    {
      question: "Can I take all three formulas together (Ova Balance, Liver Biome Plus and Mag Absorb Pro)?",
      answer: "Yes, when they are spaced across the day and any overlapping minerals are counted. One Ova sachet with breakfast, two Liver Biome Plus tablets during the day with food, and one Mag Absorb Pro tablet in the evening. That spacing is deliberate: the morning slot is metabolic, the daytime slot is liver-and-gut clearance, and the evening slot supports sleep. Speak to your clinician first if you are pregnant, breastfeeding, trying to conceive, or taking glucose-lowering medicines, blood thinners or medicines affected by magnesium.",
    },
    {
      question: "How does magnesium interact with inositol and cortisol in PCOS?",
      answer: "Magnesium is a cofactor in insulin signalling and in the stress response, and low magnesium status is common in women with PCOS. It does not act on the ovary. Its practical value is at night: better sleep quality and less evening restlessness mean you start the next day more insulin-sensitive, which is the same lever inositol works on from the other side. Ova contributes 100 mg elemental magnesium and one Mag tablet adds 220 mg, so this pair totals 320 mg per day.",
    },
    ...magnesiumQuestions,
    {
      question: kind === "liver" ? "How long will this duo last?" : "Will every product run out together?",
      answer: supply.duration,
    },
    {
      question: "Do I need all three products?",
      answer: "Choose the pair that matches your priorities. The trio makes sense only when you want the cycle, magnesium and liver-and-gut components together. A larger bundle is not automatically the right routine for everyone.",
    },
    {
      question: "Who should check suitability before starting?",
      answer: "Check with your clinician if you are pregnant, breastfeeding, trying to conceive, have kidney or gallbladder disease, or take medicines—especially glucose-lowering medicines, blood thinners or medicines affected by magnesium. Ova contains berberine; do not treat this bundle as a prenatal routine.",
    },
    {
      question: "What should I expect from using them together?",
      answer: "The bundle combines distinct support roles and gives you a clear daily routine. Track your own experience over time; these supplements do not promise a fixed date for cycle changes, symptom relief or other results.",
    },
  ];
}
